import { useCallback, useMemo } from 'react';
import {
  generatedApi,
  type PreviewInfo,
  type PreviewUpdateData,
  useDeletePreviewPreviewDeletePostMutation,
  useGetOrganizationPreviewPreviewListGetQuery,
  useUpdatePreviewPreviewUpdatePostMutation,
} from '@shared/api';
import { useAppDispatch } from '@shared/redux';
import { previewMatchesSearch } from '../lib/preview-values';

export const PREVIEWS_PAGE_SIZE = 20;

export const usePreviews = (organizationId: number | undefined, search: string, page: number) => {
  const dispatch = useAppDispatch();
  const queryArgs = useMemo(
    () => ({
      organizationId: organizationId ?? 0,
      search: search || undefined,
      limit: PREVIEWS_PAGE_SIZE,
      offset: (page - 1) * PREVIEWS_PAGE_SIZE,
    }),
    [organizationId, page, search],
  );
  const query = useGetOrganizationPreviewPreviewListGetQuery(queryArgs, {
    skip: !organizationId,
    refetchOnMountOrArgChange: true,
  });
  const [updateRequest, updateState] = useUpdatePreviewPreviewUpdatePostMutation();
  const [deleteRequest, deleteState] = useDeletePreviewPreviewDeletePostMutation();

  const prependPreview = useCallback(
    (preview: PreviewInfo) => {
      if (!organizationId) return;

      dispatch(
        generatedApi.util.updateQueryData(
          'getOrganizationPreviewPreviewListGet',
          {
            organizationId,
            search: undefined,
            limit: PREVIEWS_PAGE_SIZE,
            offset: 0,
          },
          (draft) => {
            const existingIndex = draft.items.findIndex((item) => item.id === preview.id);

            if (existingIndex >= 0) draft.items.splice(existingIndex, 1);
            else draft.count += 1;

            draft.items.unshift(preview);
            draft.items.splice(PREVIEWS_PAGE_SIZE);
          },
        ),
      );
    },
    [dispatch, organizationId],
  );

  const updatePreview = async (previewId: number, values: PreviewUpdateData) => {
    const updated = await updateRequest({ previewId, previewUpdateData: values }).unwrap();

    dispatch(
      generatedApi.util.updateQueryData(
        'getOrganizationPreviewPreviewListGet',
        queryArgs,
        (draft) => {
          const index = draft.items.findIndex((item) => item.id === previewId);
          if (index < 0) return;

          if (previewMatchesSearch(updated, search)) {
            draft.items[index] = updated;
          } else {
            draft.items.splice(index, 1);
            draft.count = Math.max(0, draft.count - 1);
          }
        },
      ),
    );

    return updated;
  };

  const deletePreview = async (previewId: number) => {
    await deleteRequest({ previewId }).unwrap();

    dispatch(
      generatedApi.util.updateQueryData(
        'getOrganizationPreviewPreviewListGet',
        queryArgs,
        (draft) => {
          const index = draft.items.findIndex((item) => item.id === previewId);
          if (index < 0) return;

          draft.items.splice(index, 1);
          draft.count = Math.max(0, draft.count - 1);
        },
      ),
    );
  };

  return {
    ...query,
    updatePreview,
    deletePreview,
    prependPreview,
    isUpdating: updateState.isLoading,
    isDeleting: deleteState.isLoading,
  };
};
